import { zodResolver } from "@hookform/resolvers/zod";
import { Announcement03 } from "@untitledui/icons";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import z from "zod/v3";
import type { TargetCollaborationDetail } from "@/api/collaborations";
import { useCollaborationSequenceOverrides, useUpdateTargetCollaboration } from "@/api/collaborations";
import { useProducts } from "@/api/products";
import { useCurrency } from "@/hooks/use-currency";
import type { StepperStep } from "@/pages/collab-groups/modals/collab-stepper-modal";
import { CollabStepperModal } from "@/pages/collab-groups/modals/collab-stepper-modal";
import type { CreateCollabFormValues, ProductEntry } from "@/pages/collab-groups/modals/create-collab-group-modal";
import { collabFormSchema } from "@/pages/collab-groups/modals/create-collab-group-modal";
import type { AutomationsFormState } from "@/pages/collab-groups/modals/steps/AutomationStep5";
import { AutomationStep5, INITIAL_AUTOMATIONS } from "@/pages/collab-groups/modals/steps/AutomationStep5";
import { DetailsStep1 } from "@/pages/collab-groups/modals/steps/DetailsStep1";
import { InvitationStep4 } from "@/pages/collab-groups/modals/steps/InvitationStep4";
import { ProductStep2 } from "@/pages/collab-groups/modals/steps/ProductStep2";
import { ReviewStep6 } from "@/pages/collab-groups/modals/steps/ReviewStep6";
import { SamplesStep3 } from "@/pages/collab-groups/modals/steps/SamplesStep3";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";

const editSchema = collabFormSchema.extend({
  invitedCreatorIds: z.array(z.string()),
  whatsappNumber: z.string(),
  activeUntil: z.string().min(1, "Required"),
});

type SequenceOverride = { triggerType: string; enabled: boolean; sequenceId: string | null };

function isoToDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function decimalToPercentString(value: number | null | undefined): string {
  if (value == null || value === 0) return "";
  return String(parseFloat((value * 100).toFixed(4)));
}

function formatPrice(prices: number[], currency: string): string {
  if (!prices.length) return "";
  const min = Math.min(...prices);
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(min);
}

function deriveApprovalRule(detail: TargetCollaborationDetail): CreateCollabFormValues["approvalRule"] {
  if (!detail.hasFreeSample) return null;
  if (detail.isSampleApprovalExempt) return "auto-approve";
  if (detail.minRequirements) return "auto-approve";
  return "manually-review";
}

function buildAutomations(overrides: SequenceOverride[]): AutomationsFormState {
  const automations = { ...INITIAL_AUTOMATIONS };
  for (const override of overrides) {
    if (override.triggerType in automations) {
      automations[override.triggerType as keyof AutomationsFormState] = {
        enabled: override.enabled,
        sequenceId: override.sequenceId,
      };
    }
  }
  return automations;
}

function buildInitialValues(detail: TargetCollaborationDetail, overrides: SequenceOverride[]): CreateCollabFormValues {
  return {
    collaborationName: detail.name,
    activeUntil: isoToDateInputValue(detail.endTime),
    products: detail.products.map((p) => ({
      id: p.productId,
      name: p.productTitle ?? "",
      thumbnail: null,
      price: "",
      commissionRate: decimalToPercentString(p.commissionRate),
      shopAdsEnabled: p.shopAdsCommissionRate != null && p.shopAdsCommissionRate > 0,
      shopAdsCommission: decimalToPercentString(p.shopAdsCommissionRate),
    })),
    invitedCreatorIds: [],
    invitedCreators: [],
    offerFreeSamples: detail.hasFreeSample ?? true,
    approvalRule: deriveApprovalRule(detail),
    minGmv: detail.minRequirements?.minGmv != null ? String(detail.minRequirements.minGmv) : "",
    minFollowers: detail.minRequirements?.minFollowers != null ? String(detail.minRequirements.minFollowers) : "",
    minPostRate: detail.minRequirements?.minPostRate != null ? String(detail.minRequirements.minPostRate) : "",
    minAvgViews: detail.minRequirements?.minAvgViews != null ? String(detail.minRequirements.minAvgViews) : "",
    categoryFit: detail.minRequirements?.categoryFit ?? "",
    invitationMessage: detail.message ?? "",
    sellerEmail: detail.sellerEmail ?? "",
    whatsappNumber: "",
    automations: buildAutomations(overrides),
  };
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collaboration: TargetCollaborationDetail;
  initialStepId?: string;
};

export const EditTargetCollaborationStepper = ({ open, onOpenChange, collaboration, initialStepId }: Props) => {
  const currency = useCurrency();
  const triggerTypes = useCollabTriggerTypes();
  const isDraft = collaboration.status === "DRAFT";

  const updateMutation = useUpdateTargetCollaboration();
  const { data: overrides = [], isSuccess: overridesLoaded } = useCollaborationSequenceOverrides(
    collaboration.collaborationId,
  );
  const { data: productsData } = useProducts();

  const form = useForm<CreateCollabFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: buildInitialValues(collaboration, overrides),
  });

  const overridesHydratedRef = useRef(false);
  const enrichedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    form.reset(buildInitialValues(collaboration, overrides));
    overridesHydratedRef.current = false;
    enrichedRef.current = false;
  }, [open, collaboration]);

  useEffect(() => {
    if (!open || overridesHydratedRef.current || !overridesLoaded) return;
    overridesHydratedRef.current = true;
    form.setValue("automations", buildAutomations(overrides));
  }, [overrides, open, overridesLoaded, form]);

  useEffect(() => {
    if (!productsData || enrichedRef.current || !open) return;
    enrichedRef.current = true;

    const productMap = new Map(productsData.map((p) => [p.productId, p]));
    const current = form.getValues("products");
    const needsUpdate = current.some((p) => (!p.thumbnail || !p.price || !p.name) && productMap.has(p.id));
    if (!needsUpdate) return;

    form.setValue(
      "products",
      current.map((entry): ProductEntry => {
        const match = productMap.get(entry.id);
        if (!match || (entry.thumbnail && entry.price && entry.name)) return entry;
        return {
          ...entry,
          name: entry.name || match.productName,
          thumbnail: entry.thumbnail || match.thumbnail,
          price: entry.price || formatPrice(match.prices, currency),
        };
      }),
    );
  }, [productsData, open, currency, form]);

  const steps: StepperStep<CreateCollabFormValues>[] = [
    {
      id: "details",
      label: "Details",
      render: () => <DetailsStep1 />,
      fields: ["collaborationName", "activeUntil"],
    },
    { id: "product", label: "Product", render: () => <ProductStep2 />, fields: ["products"] },
    { id: "samples", label: "Samples", render: () => <SamplesStep3 /> },
    {
      id: "invitation",
      label: "Invitation",
      render: () => <InvitationStep4 showCreatorSelection={false} showWhatsapp={false} messageReadOnly={!isDraft} />,
      fields: ["sellerEmail", "invitationMessage"],
    },
    { id: "automation", label: "Automation", render: () => <AutomationStep5 /> },
    { id: "review", label: "Review", render: () => <ReviewStep6 showInvitedCreators={false} /> },
  ];

  const onSubmit = async (values: CreateCollabFormValues) => {
    const hasMinRequirements =
      values.offerFreeSamples &&
      values.approvalRule === "auto-approve" &&
      (values.minGmv !== "" ||
        values.minFollowers !== "" ||
        values.minPostRate !== "" ||
        values.minAvgViews !== "" ||
        values.categoryFit !== "");

    try {
      await updateMutation.mutateAsync({
        collaborationId: collaboration.collaborationId,
        name: values.collaborationName.trim(),
        endTime: new Date(values.activeUntil).toISOString(),
        products: values.products.map((p) => ({
          id: p.id,
          targetCommissionRate: Number(p.commissionRate),
          ...(p.shopAdsEnabled && p.shopAdsCommission !== ""
            ? { shopAdsCommissionRate: Number(p.shopAdsCommission) }
            : {}),
        })),
        sellerEmail: values.sellerEmail.trim(),
        ...(isDraft && values.invitationMessage.trim() ? { message: values.invitationMessage.trim() } : {}),
        freeSampleRule: {
          hasFreeSample: values.offerFreeSamples,
          isSampleApprovalExempt:
            values.offerFreeSamples && values.approvalRule === "auto-approve" && !hasMinRequirements,
        },
        minRequirements: hasMinRequirements
          ? {
              ...(values.minGmv !== "" ? { minGmv: Number(values.minGmv) } : {}),
              ...(values.minFollowers !== "" ? { minFollowers: Number(values.minFollowers) } : {}),
              ...(values.minPostRate !== "" ? { minPostRate: Number(values.minPostRate) } : {}),
              ...(values.minAvgViews !== "" ? { minAvgViews: Number(values.minAvgViews) } : {}),
              ...(values.categoryFit !== "" ? { categoryFit: values.categoryFit } : {}),
            }
          : undefined,
        automations: Object.fromEntries(
          triggerTypes.map((triggerType) => [triggerType, values.automations[triggerType]]),
        ),
      });

      toastSuccess("Collaboration updated");
      onOpenChange(false);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Failed to update collaboration");
    }
  };

  return (
    <CollabStepperModal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Target Collaboration"
      subtitle={collaboration.name}
      icon={Announcement03}
      form={form}
      steps={steps}
      onSubmit={onSubmit}
      submitLabel="Save changes"
      submitPendingLabel="Saving…"
      isSubmitting={updateMutation.isPending}
      allowStepJump
      initialStepId={initialStepId}
      submitFromAnyStep
    />
  );
};
